import { DUSK_CHAIN_PRESETS } from "@dusk/connect";
import { Button, TextField, ActionBar, ErrorBanner } from "frontend-kit/components/index.js";
import {
  createParticipant,
  getParticipant,
  getParticipantByAccount,
  linkParticipantCommitment,
  loadBackendConfig,
  verifyParticipant,
} from "./gateway-api.js";
import {
  isParticipantReady,
  loadParticipant,
  saveParticipant,
} from "./participant-store.js";
import { clearIdentitySession } from "./session-identity.js";
import {
  clearTradingKey,
  downloadTradingKeyBackup,
  importTradingKeyBackup,
  loadTradingKey,
  saveTradingKey,
} from "./trading-key-store.js";
import { deriveTradingKeyFromWallet } from "./trader-crypto.js";
import { PRIVACY_BLURB } from "./privacy-copy.js";
import {
  assertWalletOnTestnet,
  extractBoundParticipantId,
  humanizeVerifyError,
  normalizeTxHash,
  verifyParticipantWithRetry,
} from "./onboarding-verify.js";
import { connectTradingKeyFlow } from "./onboarding-trading-key.js";
import { renderTxCell } from "./tx-cell.js";
import { getChainApp } from "./chain-app.js";

function paragraph(text) {
  const p = document.createElement("p");
  p.className = "hint";
  p.textContent = text;
  return p;
}

function codeLine(text) {
  const pre = document.createElement("pre");
  pre.className = "participant-memo";
  pre.textContent = text;
  return pre;
}

function shortFact(prefix, value, len = 16) {
  if (!value) return prefix;
  return `${prefix} ${String(value).slice(0, len)}…`;
}

/**
 * Soft onboarding: participant id → Moonlight self-transfer memo proof →
 * trading key link. Not Citadel KYC.
 *
 * Three always-visible steps; only the active step gets a primary CTA.
 * Parent `onChange` must refresh Status / wallet chrome only - never remount.
 */
export function renderParticipantPanel({ wallet, log, onChange }) {
  const wrap = document.createElement("div");
  wrap.className = "participant-panel";

  let record = loadParticipant();
  let busyLabel = null;
  /** @type {string | null} */
  let pendingProofTxHash = record?.pending_proof_tx_hash || null;

  const errorHost = document.createElement("div");
  errorHost.className = "participant-error-host";
  wrap.append(errorHost);

  const stepsHost = document.createElement("div");
  stepsHost.className = "settings-step-list";
  wrap.append(stepsHost);

  const secondaryHost = document.createElement("div");
  secondaryHost.className = "settings-secondary";
  wrap.append(secondaryHost);

  function setError(msg, opts = {}) {
    if (!msg) {
      errorHost.replaceChildren("");
      return;
    }
    const raw = opts.raw ? String(opts.raw).trim() : "";
    const detail =
      raw && raw !== msg && !msg.includes(raw)
        ? `${msg}\n\nGateway: ${raw}`
        : msg;
    const nodes = [ErrorBanner({ message: detail })];
    const boundId =
      opts.boundId ??
      extractBoundParticipantId(msg) ??
      extractBoundParticipantId(raw);
    if (boundId) {
      nodes.push(
        ActionBar([
          Button({
            label: "Resume bound participant",
            onClick: async () => {
              setError(null);
              setBusy("Resuming…");
              renderSteps();
              try {
                await resumeBoundParticipant(boundId);
              } catch (e) {
                setError(e.message || String(e));
                log?.(`resume bound participant failed: ${e.message || e}`);
              } finally {
                setBusy(null);
                renderSteps();
              }
            },
          }),
        ])
      );
    }
    errorHost.replaceChildren(...nodes);
  }

  async function resumeBoundParticipant(participantId) {
    const cfg = loadBackendConfig();
    const account = wallet?.state?.profiles?.[0]?.account;
    let existing = null;
    if (account) {
      existing = await getParticipantByAccount(cfg, account);
    }
    if (!existing?.participant_id || existing.participant_id !== participantId) {
      existing = await getParticipant(cfg, participantId);
    }
    if (!existing?.participant_id) {
      throw new Error("Bound participant not found on gateway");
    }
    saveParticipant(existing);
    pendingProofTxHash = existing.pending_proof_tx_hash || null;
    log?.(`resumed bound participant ${existing.participant_id}`);
    notify();
    return existing;
  }

  function setBusy(label) {
    busyLabel = label;
  }

  function notify() {
    onChange?.();
  }

  async function ensureParticipant() {
    const cfg = loadBackendConfig();
    const account = wallet?.state?.profiles?.[0]?.account;
    if (account) {
      const existing = await getParticipantByAccount(cfg, account);
      if (existing?.participant_id) {
        saveParticipant(existing);
        log?.(
          `resumed participant ${existing.participant_id} already bound to this Moonlight account`
        );
        notify();
        return existing;
      }
    }
    const created = await createParticipant(cfg);
    saveParticipant(created);
    log?.(`participant ${created.participant_id} created`);
    notify();
    return created;
  }

  async function resumeIfWalletBound() {
    const account = wallet?.state?.profiles?.[0]?.account;
    if (!account) return null;
    const local = loadParticipant();
    if (local?.status === "verified" && local.moonlight_account === account) {
      return local;
    }
    try {
      const cfg = loadBackendConfig();
      const existing = await getParticipantByAccount(cfg, account);
      if (!existing?.participant_id) return null;
      if (
        local?.participant_id &&
        local.participant_id !== existing.participant_id &&
        local.status !== "verified"
      ) {
        log?.(
          `local pending ${local.participant_id} replaced by verified ${existing.participant_id} for this wallet`
        );
      }
      saveParticipant(existing);
      notify();
      return existing;
    } catch (e) {
      const msg = e?.message || String(e);
      console.warn("resumeIfWalletBound failed:", msg);
      setError(`Could not look up wallet binding: ${msg}`);
      return null;
    }
  }

  async function syncFromGateway() {
    const local = loadParticipant();
    if (!local?.participant_id) return null;
    const cfg = loadBackendConfig();
    const remote = await getParticipant(cfg, local.participant_id);
    saveParticipant({ ...local, ...remote });
    return remote;
  }

  /**
   * Derives the trading key from the connected wallet (deterministic) and
   * links it to the participant. Capability check, signature timeout, and
   * refuse-overwrite live in connectTradingKeyFlow.
   */
  async function connectTradingKey() {
    const before = loadParticipant();
    const { key, participant } = await connectTradingKeyFlow({
      wallet,
      deriveTradingKey: deriveTradingKeyFromWallet,
      loadParticipant,
      saveParticipant,
      saveTradingKey,
      linkCommitment: linkParticipantCommitment,
      loadBackendConfig,
    });
    if (!before?.trading_commitment_hex) {
      log?.(
        `participant ${participant.participant_id} linked to wallet-derived commitment`
      );
    } else {
      log?.("wallet-derived trading key matches the linked commitment");
    }
    return key;
  }

  function primaryButton({ label, disabled, onClick }) {
    const isBusy = Boolean(busyLabel);
    return Button({
      label: isBusy ? busyLabel : label,
      variant: "primary",
      disabled: isBusy || disabled,
      onClick,
    });
  }

  function makeStep({ n, title, done, active, fact, body }) {
    const step = document.createElement("div");
    step.className = "settings-step";
    if (done) step.classList.add("is-done");
    if (active) step.classList.add("is-active");

    const head = document.createElement("div");
    head.className = "settings-step-head";
    const mark = document.createElement("span");
    mark.className = "settings-step-mark";
    mark.textContent = done ? "✓" : String(n);
    mark.setAttribute("aria-hidden", "true");
    const label = document.createElement("span");
    label.textContent = title;
    head.append(mark, label);
    if (done && fact) {
      const factEl = document.createElement("span");
      factEl.className = "settings-step-fact";
      factEl.textContent = fact;
      head.append(factEl);
    }
    step.append(head);

    if (body) {
      const bodyEl = document.createElement("div");
      bodyEl.className = "settings-step-body";
      if (Array.isArray(body)) bodyEl.append(...body.filter(Boolean));
      else bodyEl.append(body);
      step.append(bodyEl);
    }
    return step;
  }

  function renderSteps() {
    record = loadParticipant();
    const key = loadTradingKey();
    const connected = Boolean(wallet?.state?.authorized && wallet.state.profiles?.length);
    const account = wallet?.state?.profiles?.[0]?.account;

    const step1Done = Boolean(record);
    const step2Done = record?.status === "verified";
    const step3Done =
      Boolean(record?.status === "verified") &&
      isParticipantReady(record) &&
      Boolean(key?.commitment_hex) &&
      Boolean(record?.trading_commitment_hex) &&
      key.commitment_hex.toLowerCase() ===
        record.trading_commitment_hex.toLowerCase();

    const step1Active = !record;
    const step2Active = Boolean(record) && record.status !== "verified";
    const step3Active = record?.status === "verified" && !step3Done;

    const steps = [];

    // -- Step 1: Participant id --
    const step1Body = [];
    if (step1Active) {
      step1Body.push(
        primaryButton({
          label: "Get participant id",
          onClick: async () => {
            setError(null);
            setBusy("Working…");
            renderSteps();
            try {
              await ensureParticipant();
            } catch (e) {
              setError(e.message || String(e));
              log?.(`create participant failed: ${e.message || e}`);
            } finally {
              setBusy(null);
              renderSteps();
            }
          },
        })
      );
    }
    steps.push(
      makeStep({
        n: 1,
        title: "Participant id",
        done: step1Done,
        active: step1Active,
        fact: record ? shortFact("Id", record.participant_id, 20) : null,
        body: step1Body.length ? step1Body : null,
      })
    );

    // -- Step 2: Prove wallet --
    const step2Body = [];
    if (step2Active && record) {
      const memo = record.memo || `pm-participant-v1:${record.participant_id}`;
      step2Body.push(paragraph("Memo for self-transfer:"));
      step2Body.push(codeLine(memo));

      if (!connected) {
        step2Body.push(
          paragraph(
            "Connect your Moonlight wallet from the account menu first"
          )
        );
      }

      if (pendingProofTxHash) {
        const hashRow = document.createElement("div");
        hashRow.className = "participant-pending-hash";
        hashRow.append(
          paragraph("Pending proof tx:"),
          renderTxCell(pendingProofTxHash, { keep: 10 })
        );
        step2Body.push(hashRow);
        step2Body.push(
          primaryButton({
            label: "Verify again",
            disabled: Boolean(busyLabel) || !connected || !account,
            onClick: async () => {
              setError(null);
              setBusy("Verifying…");
              renderSteps();
              try {
                // One-shot: show the real gateway error immediately.
                await runVerify(pendingProofTxHash, { attempts: 1 });
              } catch (e) {
                setError(humanizeVerifyError(e), {
                  raw: e?.message || String(e),
                });
                log?.(`participant verify failed: ${e.message || e}`);
              } finally {
                setBusy(null);
                renderSteps();
              }
            },
          })
        );
      } else {
        step2Body.push(
          primaryButton({
            label: "Send self-transfer proof",
            disabled: Boolean(busyLabel) || !connected || !account,
            onClick: async () => {
              setError(null);
              setBusy("Waiting for wallet…");
              renderSteps();
              let sentHash = null;
              try {
                if (!connected || !account) {
                  throw new Error("Connect your Moonlight wallet first");
                }
                assertWalletOnTestnet(wallet);
                setBusy("Ensuring testnet…");
                renderSteps();
                const dusk = await getChainApp();
                await dusk.ensureChain({ chainId: DUSK_CHAIN_PRESETS.testnet });
                assertWalletOnTestnet(dusk.wallet ?? wallet);
                setBusy("Waiting for wallet…");
                renderSteps();
                const tx = await wallet.sendTransfer({
                  privacy: "public",
                  to: account,
                  amount: "1",
                  memo,
                });
                const txHash = normalizeTxHash(tx?.hash);
                sentHash = txHash;
                persistPendingHash(txHash);
                log?.(`participant proof tx ${txHash} - waiting for inclusion, then verify`);
                // Keep busy through auto-verify (do not clear / re-enable Verify mid-poll).
                await runVerify(txHash);
              } catch (e) {
                setError(
                  sentHash ? humanizeVerifyError(e) : e.message || String(e),
                  { raw: e?.message || String(e) }
                );
                log?.(`participant verify failed: ${e.message || e}`);
              } finally {
                setBusy(null);
                renderSteps();
              }
            },
          })
        );
      }

      const manual = document.createElement("div");
      manual.className = "participant-manual";
      const field = TextField({
        id: "participant-tx-hash",
        label: "Proof tx hash",
        value: pendingProofTxHash || "",
      });
      manual.append(field);
      manual.append(
        ActionBar([
          Button({
            label: "Verify pasted hash",
            disabled: Boolean(busyLabel) || !connected || !account,
            onClick: async () => {
              setError(null);
              setBusy("Verifying…");
              renderSteps();
              try {
                const input = field.querySelector("input");
                const txHash = normalizeTxHash(input?.value);
                persistPendingHash(txHash);
                await runVerify(txHash, { attempts: 1 });
              } catch (e) {
                setError(humanizeVerifyError(e), {
                  raw: e?.message || String(e),
                });
                log?.(`participant verify failed: ${e.message || e}`);
              } finally {
                setBusy(null);
                renderSteps();
              }
            },
          }),
        ])
      );
      step2Body.push(manual);
    }
    steps.push(
      makeStep({
        n: 2,
        title: "Prove wallet",
        done: step2Done,
        active: step2Active,
        fact: step2Done
          ? shortFact("Tx", record?.proof_tx_hash || pendingProofTxHash)
          : null,
        body: step2Body.length ? step2Body : null,
      })
    );

    // -- Step 3: Trading key --
    const step3Body = [];
    if (step3Active) {
      step3Body.push(paragraph(PRIVACY_BLURB));
      step3Body.push(
        paragraph(
          "The key is derived per site origin. Localhost and the hosted site produce different keys."
        )
      );
      if (!connected) {
        step3Body.push(
          paragraph(
            "Connect your Moonlight wallet from the account menu first"
          )
        );
      }
      step3Body.push(
        primaryButton({
          label: "Connect trading key",
          disabled: !connected,
          onClick: async () => {
            setError(null);
            setBusy("Waiting for wallet signature…");
            renderSteps();
            try {
              await connectTradingKey();
              notify();
            } catch (e) {
              setError(e.message || String(e));
              log?.(`connect trading key failed: ${e.message || e}`);
            } finally {
              setBusy(null);
              renderSteps();
            }
          },
        })
      );
    } else if (step3Done && key) {
      step3Body.push(
        paragraph(
          `Commitment ${key.commitment_hex?.slice(0, 16)}… · pk ${key.pk_base58?.slice(0, 12)}…`
        )
      );
    }

    // Backup / restore always available (not gated on verified) so a cleared
    // participant or shared machine can still Import without re-soft-onboarding
    // first. Reconnect stays verified-only (needs gateway link).
    const keySecondary = [];
    if (record?.status === "verified" && step3Done) {
      keySecondary.push(
        Button({
          label: "Reconnect trading key",
          disabled: Boolean(busyLabel) || !connected,
          onClick: async () => {
            setError(null);
            setBusy("Waiting for wallet signature…");
            renderSteps();
            try {
              await connectTradingKey();
              log?.("trading key derived from wallet signature");
              notify();
            } catch (e) {
              setError(e.message || String(e));
              log?.(`connect trading key failed: ${e.message || e}`);
            } finally {
              setBusy(null);
              renderSteps();
            }
          },
        })
      );
    }
    keySecondary.push(
      Button({
        label: "Import backup",
        disabled: Boolean(busyLabel),
        onClick: () => {
          setError(null);
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "application/json,.json";
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
              const text = await file.text();
              const imported = importTradingKeyBackup({
                text,
                loadParticipant,
                saveTradingKey,
              });
              log?.(
                `trading key imported from backup (${imported.commitment_hex.slice(0, 16)}…)`
              );
              notify();
              renderSteps();
            } catch (e) {
              setError(e.message || String(e));
              log?.(`import trading key failed: ${e.message || e}`);
            }
          };
          input.click();
        },
      }),
      Button({
        label: "Export backup",
        disabled: Boolean(busyLabel) || !key,
        onClick: () => {
          setError(null);
          try {
            if (key) downloadTradingKeyBackup(key);
          } catch (e) {
            setError(e.message || String(e));
          }
        },
      }),
      Button({
        label: "Clear key",
        variant: "danger",
        disabled: Boolean(busyLabel) || !key,
        onClick: () => {
          setError(null);
          clearTradingKey();
          log?.(
            "trading key cleared from this browser - reconnect to re-derive it from your wallet"
          );
          notify();
          renderSteps();
        },
      })
    );
    if (!step3Body.length && !record) {
      step3Body.push(
        paragraph(
          "Import a trading-key backup JSON anytime. Connect + verify your Moonlight wallet before claiming or trading."
        )
      );
    }
    step3Body.push(ActionBar(keySecondary));

    steps.push(
      makeStep({
        n: 3,
        title: "Trading key",
        done: step3Done,
        active: step3Active,
        fact: step3Done
          ? shortFact("Commitment", key?.commitment_hex || record?.trading_commitment_hex)
          : null,
        body: step3Body.length ? step3Body : null,
      })
    );

    stepsHost.replaceChildren(...steps);

    // Secondary: Refresh / Clear participant
    const secondary = [];
    if (record) {
      secondary.push(
        Button({
          label: "Refresh status",
          disabled: Boolean(busyLabel),
          onClick: async () => {
            setError(null);
            setBusy("Working…");
            renderSteps();
            try {
              await syncFromGateway();
              notify();
            } catch (e) {
              setError(e.message || String(e));
              log?.(`refresh participant failed: ${e.message || e}`);
            } finally {
              setBusy(null);
              renderSteps();
            }
          },
        }),
        Button({
          label: "Clear participant",
          variant: "danger",
          disabled: Boolean(busyLabel),
          onClick: () => {
            setError(null);
            clearIdentitySession();
            pendingProofTxHash = null;
            log?.(
              "participant and trading key cleared from localStorage"
            );
            notify();
            renderSteps();
          },
        })
      );
    }
    secondaryHost.replaceChildren(secondary.length ? ActionBar(secondary) : "");
  }

  function persistPendingHash(txHash) {
    pendingProofTxHash = txHash;
    const local = loadParticipant();
    if (local) {
      saveParticipant({ ...local, pending_proof_tx_hash: txHash });
    }
  }

  async function runVerify(txHash, { attempts = 4 } = {}) {
    assertWalletOnTestnet(wallet);
    const account = wallet?.state?.profiles?.[0]?.account;
    if (!account) throw new Error("Connect wallet first");
    const current = loadParticipant();
    if (!current?.participant_id) throw new Error("No participant id");

    const cfg = loadBackendConfig();
    const verified = await verifyParticipantWithRetry({
      attempts,
      body: {
        participant_id: current.participant_id,
        tx_hash: txHash,
        moonlight_account: account,
      },
      verifyFn: (body) => verifyParticipant(cfg, body),
      onWait: () => {
        setBusy("Waiting for next block…");
        renderSteps();
      },
      onAttempt: (n, total) => {
        setBusy(total > 1 ? `Verifying (${n}/${total})…` : "Verifying…");
        renderSteps();
      },
    });
    const saved = {
      ...verified,
      proof_tx_hash: verified.proof_tx_hash || txHash,
    };
    delete saved.pending_proof_tx_hash;
    saveParticipant(saved);
    pendingProofTxHash = null;
    log?.(`participant ${current.participant_id} verified`);
    notify();
  }

  renderSteps();
  void resumeIfWalletBound().then((resumed) => {
    if (resumed) {
      pendingProofTxHash = resumed.pending_proof_tx_hash || pendingProofTxHash;
      renderSteps();
    }
  });

  return wrap;
}
